import React, { useState } from 'react';
import { NavLink, BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import {
    HiOutlineHome,
    HiOutlineChatBubbleLeftRight,
    HiOutlineUserGroup,
    HiOutlineDocumentText,
    HiArrowLeftOnRectangle,
    HiOutlineUserCircle,
    HiOutlineChartBar,
    HiOutlineBars3,
    HiOutlineShieldCheck,
} from 'react-icons/hi2';

// Contexte et Route Protégée
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';

// Import de toutes les pages
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import ChatbotPage from './pages/ChatbotPage';
import CandidatsPage from './pages/CandidatsPage';
import AjouterCandidatPage from './pages/AjouterCandidatPage';
import ModifierCandidatPage from './pages/ModifierCandidatPage';
import OffresPage from './pages/OffresPage';
import AjouterOffrePage from './pages/AjouterOffrePage';
import ModifierOffrePage from './pages/ModifierOffrePage';
import MatchingPage from './pages/MatchingPage';
import MatchingOffresPage from './pages/MatchingOffresPage';
import AnalyticsPage from './pages/AnalyticsPage';
import CoverageInfo from './pages/CoverageInfo';
import AdminDashboard from './pages/AdminDashboard';
import UnauthorizedPage from './pages/UnauthorizedPage';

// Le contenu de l'application est dans un composant séparé
const AppContent = () => {
    const { currentUser, logout, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = async () => {
        try {
            await logout();
            toast.success('Vous avez été déconnecté.');
            navigate('/login');
        } catch (error) {
            toast.error('Erreur lors de la déconnexion.');
        }
    };

    const NavItem = ({ to, icon, label }) => {
        const activeClassName = "bg-pro-dark-accent text-white";
        const inactiveClassName = "text-pro-dark-text-secondary hover:bg-pro-dark-border hover:text-pro-dark-text-primary";

        return (
            <li>
                <NavLink to={to} className={({ isActive }) => `flex items-center p-3 my-1 rounded-lg transition-colors duration-200 ${isActive ? activeClassName : inactiveClassName}`}>
                    {icon}
                    <span className="ml-4 font-medium">{label}</span>
                </NavLink>
            </li>
        );
    };

    return (
        <div className="flex flex-col md:flex-row h-screen bg-pro-dark-bg text-pro-dark-text-primary">
            {currentUser && (
                <>
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className="p-3 md:hidden text-pro-dark-text-primary"
                        aria-label="Ouvrir le menu"
                    >
                        <HiOutlineBars3 size={24} />
                    </button>
                    <nav
                        className={`fixed inset-y-0 left-0 z-20 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-200 md:relative md:translate-x-0 w-64 md:w-72 bg-pro-dark-surface p-4 flex flex-col justify-between`}
                    >
                        <div>
                            <div className="p-3 mb-6">
                                <h1 className="text-2xl font-bold text-white">Matching App</h1>
                            </div>
                            <ul>
                                <NavItem to="/" icon={<HiOutlineHome size={20} />} label="Tableau de Bord" />
                                <NavItem to="/chatbot" icon={<HiOutlineChatBubbleLeftRight size={20} />} label="Chatbot" />
                                <NavItem to="/candidats" icon={<HiOutlineUserGroup size={20} />} label="Candidats" />
                                <NavItem to="/offres" icon={<HiOutlineDocumentText size={20} />} label="Appels d’offres" />
                                <NavItem to="/analytics" icon={<HiOutlineChartBar size={20} />} label="Analytics" />
                                {isAdmin && (
                                    <NavItem to="/admin" icon={<HiOutlineShieldCheck size={20} />} label="Administration" />
                                )}
                            </ul>
                        </div>
                        <div>
                             <div className="border-t border-pro-dark-border mt-2 pt-4">
                                <div className="flex items-center p-3">
                                    <HiOutlineUserCircle size={32} className="text-pro-dark-text-secondary"/>
                                    <div className="ml-4">
                                        <p className="font-semibold text-sm text-pro-dark-text-primary">{currentUser.displayName || 'Utilisateur'}</p>
                                        <p className="text-xs text-pro-dark-text-secondary">{isAdmin ? 'Admin' : 'Utilisateur'}</p>
                                    </div>
                                </div>
                             </div>
                             <ul>
                                <li>
                                    <button onClick={handleLogout} className="flex items-center p-3 my-1 rounded-lg w-full text-pro-dark-text-secondary hover:bg-pro-dark-border hover:text-pro-dark-text-primary transition-colors duration-200">
                                        <HiArrowLeftOnRectangle size={20} />
                                        <span className="ml-4 font-medium">Déconnexion</span>
                                    </button>
                                </li>
                             </ul>
                        </div>
                    </nav>
                    {sidebarOpen && (
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 z-10 md:hidden"
                            onClick={() => setSidebarOpen(false)}
                        />
                    )}
                </>
            )}

            <main className="flex-1 p-4 md:p-12 overflow-auto" onClick={() => sidebarOpen && setSidebarOpen(false)}>
                <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
                    <Route path="/chatbot" element={<ProtectedRoute><ChatbotPage /></ProtectedRoute>} />
                    <Route path="/candidats" element={<ProtectedRoute><CandidatsPage /></ProtectedRoute>} />
                    <Route path="/candidats/ajouter" element={<ProtectedRoute><AjouterCandidatPage /></ProtectedRoute>} />
                    <Route path="/candidats/modifier/:id" element={<ProtectedRoute><ModifierCandidatPage /></ProtectedRoute>} />
                    <Route path="/candidats/:id/matching" element={<ProtectedRoute><MatchingOffresPage /></ProtectedRoute>} />
                    <Route path="/offres" element={<ProtectedRoute><OffresPage /></ProtectedRoute>} />
                    <Route path="/offres/ajouter" element={<ProtectedRoute><AjouterOffrePage /></ProtectedRoute>} />
                    <Route path="/offres/modifier/:id" element={<ProtectedRoute><ModifierOffrePage /></ProtectedRoute>} />
                    <Route path="/offres/:id/matching" element={<ProtectedRoute><MatchingPage /></ProtectedRoute>} />
                    <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
                    <Route path="/coverage-info" element={<ProtectedRoute><CoverageInfo /></ProtectedRoute>} />
                    <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                    <Route path="/unauthorized" element={<UnauthorizedPage />} />
                    <Route path="*" element={<Navigate to={currentUser ? "/" : "/login"} />} />
                </Routes>
            </main>
        </div>
    );
};

// Le composant App principal qui enveloppe l'application
const App = () => {
    return (
        <Router>
            <AuthProvider>
                <Toaster 
                    position="top-right"
                    toastOptions={{
                        success: { style: { background: '#10B981', color: 'white' } },
                        error: { style: { background: '#EF4444', color: 'white' } },
                    }}
                />
                <AppContent />
            </AuthProvider>
        </Router>
    );
};

export default App;
